import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Pre-construction step 1 — the Budget template card (prototype A, step 1).
// Both views are on the page, so By bid package / By division, searching,
// the filter chips, CSI order / Order of construction, the column chooser,
// opening a division and opening a cost code's bid template all happen
// here at once. Ticking a cost code or a division shows right away; the
// server confirms with the re-rendered panel (on failure the panel comes
// back as it was, with an error toast). Pop-ups: Order of construction, Bid
// template, Bid templates on/off, Add a line item, Move a cost code (owner's
// form). What the page remembers while you work (search, open rows) lives
// in STATE, so a re-rendered panel comes back the same; the view and the
// columns are per person (this browser).
const STATE = {}
const CSI = { list: null, loading: null }
// The open pop-up: kept outside the panel, so it survives the panel being
// re-rendered under it (any step 1 controller can close it).
const MODAL = { el: null }
const COLS_DEFAULT = { code: true, type: false, src: true, est: true, ai: false, variance: true }
const COL_KEYS = ["code", "type", "src", "est", "ai", "variance"]

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
const money = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("en-US")
const store = {
  get(k) { try { return window.localStorage.getItem(k) } catch (e) { return null } },
  set(k, v) { try { window.localStorage.setItem(k, v) } catch (e) { /* not kept */ } }
}
const SVG = {
  x: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"></path></svg>`,
  up: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4.5 15.75 7.5-7.5 7.5 7.5"></path></svg>`,
  down: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m19.5 8.25-7.5 7.5-7.5-7.5"></path></svg>`,
  search: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"></path></svg>`,
  sparkles: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"></path></svg>`,
  warn: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"></path></svg>`
}

export default class extends Controller {
  static targets = ["card", "cardSub", "query", "clear", "hits", "divs", "empty", "pkgs", "colBtn", "colMenu"]
  static values = { project: Number, sort: String, sf: Number, where: String }

  connect() {
    // Words, the order of construction and the bid templates come in one JSON
    // block each (smaller than data attributes).
    this._json = {}
    try { this._json = JSON.parse(this.element.querySelector("script[data-s1-json]")?.textContent || "{}") } catch (e) { this._json = {} }
    try { this._tpls = JSON.parse(this.element.querySelector("script[data-s1-tpl]")?.textContent || "{}") } catch (e) { this._tpls = {} }
    this.s = STATE[this.projectValue] ||= { query: "", filter: "all", open: {}, shut: {}, keep: {}, tpl: {}, pending: 0 }
    this.s.sort = this.sortValue
    const saved = store.get("conslog.app.precon.s1view")
    if ((saved === "package" || saved === "division") && saved !== this.element.dataset.view && this.element.querySelector("[data-s1-view='package'] .bpwrap")) this._setView(saved)
    if (this.hasQueryTarget && this.s.query) this.queryTarget.value = this.s.query
    this.element.querySelectorAll(".fchip[data-filter]").forEach(c => c.classList.toggle("on", c.dataset.filter === this.s.filter))
    this._applyCols()
    this._apply()
    this._pageCtl = this.page
    this._keydown = (e) => { if (e.key === "Escape" && MODAL.el) { e.stopPropagation(); this._closeModal() } }
    document.addEventListener("keydown", this._keydown, true)
    // The full CSI list for "Add a line item", fetched once while the page is idle.
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500))
    idle(() => this._loadCsi())
  }

  disconnect() {
    document.removeEventListener("keydown", this._keydown, true)
  }

  // The page's precon controller (kept, so a pop-up still works after the
  // panel under it was re-rendered).
  get page() {
    const el = this.element.closest("[data-controller~='precon']")
    const ctl = el ? this.application.getControllerForElementAndIdentifier(el, "precon") : null
    return ctl || this._pageCtl || null
  }

  _t(key, vars = {}) {
    let s = key.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), this._json.strings || {})
    if (s && typeof s === "object") s = vars.count === 1 ? s.one : s.other
    if (typeof s !== "string") return key
    return s.replace(/%\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : ""))
  }

  stop(e) { e.stopPropagation() }

  // ── Server calls: optimistic on the page, the panel confirms ───────────
  async _send(method, path, body, ok) {
    const page = this.page
    if (!page) return null
    this.s.pending += 1
    try {
      const q = page._viewQuery
      const data = await page._json(method, `${page.baseUrlValue}/${path}${q ? (path.includes("?") ? "&" : "?") + q : ""}`, body)
      this.s.pending -= 1
      // Several quick clicks: only the last answer is drawn.
      if (this.s.pending === 0) page._applyResponse(data)
      if (ok) page._showToast(typeof ok === "function" ? ok(data) : ok)
      return data
    } catch (e) {
      this.s.pending -= 1
      page._showToast(e.message, "error")
      if (this.s.pending === 0) page.refreshPanel?.(1)
      return null
    }
  }

  // ── By bid package | By division ──────────────────────────────────────
  switchView(e) {
    const view = e.currentTarget.dataset.view
    this._setView(view)
    store.set("conslog.app.precon.s1view", view)
    track("Precon Budget View Switched", { project_id: this.projectValue, view })
  }

  _setView(view) {
    this.element.dataset.view = view
    this.element.querySelectorAll(".bpseg .segbtn").forEach(b => { const on = b.dataset.view === view; b.classList.toggle("on", on); b.setAttribute("aria-pressed", on) })
    this.element.querySelectorAll("[data-s1-view]").forEach(el => el.classList.toggle("hidden", el.dataset.s1View !== view))
    this.element.querySelectorAll("[data-s1-only='division']").forEach(el => el.classList.toggle("hidden", view !== "division" || (el === this.hitsTarget && !this.s.query.trim())))
    if (this.hasCardSubTarget) this.cardSubTarget.textContent = view === "package" ? this._t("sub_package") : this._t("sub_division")
    if (view !== "division" && this.hasColMenuTarget) this.colMenuTarget.classList.add("hidden")
  }

  // ── Search, filter, open/close ────────────────────────────────────────
  search(e) {
    this.s.query = e.target.value
    this.s.shut = {}
    this._apply()
  }

  searchKey(e) { if (e.key === "Escape") { e.stopPropagation(); this.clearSearch() } }

  clearSearch() {
    this.s.query = ""
    this.s.shut = {}
    if (this.hasQueryTarget) this.queryTarget.value = ""
    this._apply()
  }

  filter(e) {
    this.s.filter = e.currentTarget.dataset.filter
    this.s.keep = {}
    this.s.shut = {}
    this.element.querySelectorAll(".fchip[data-filter]").forEach(c => c.classList.toggle("on", c === e.currentTarget))
    this._apply()
  }

  // One listener for the whole division table (rows are many; no per-row actions).
  divsClick(e) {
    const t = e.target
    if (t.closest("label.cb")) return
    if (t.closest("[data-pick]")) return this.openPick(t.closest(".s1grp"))
    if (t.closest("[data-tpl-edit]")) return this.openTemplate(t.closest(".t1tpl"))
    const chip = t.closest(".tplchip")
    if (chip) return this._toggleTpl(chip.closest(".t1.pick").dataset.id)
    const divrow = t.closest(".divrow")
    if (divrow) return this._divClick(divrow.closest(".s1grp"))
    const row = t.closest(".t1.pick")
    if (!row) return
    if (row.dataset.st === "on" && row.querySelector(".tplchip")) return this._toggleTpl(row.dataset.id)
    this._flip(row)
  }

  divsChange(e) {
    const cb = e.target
    if (cb.type !== "checkbox") return
    if (cb.closest(".divrow")) return this._divToggle(cb)
    const row = cb.closest(".t1.pick")
    if (row) this._flip(row, cb.checked)
  }

  _divClick(g) {
    const div = g.dataset.div
    if (this.s.query.trim()) {
      this.s.shut[div] = !this.s.shut[div]
    } else {
      this.s.open[div] = !this.s.open[div]
      // Opening or closing drops rows only kept on screen after being unchecked
      // (on Selected divisions they stay until the filter changes).
      if (this.s.filter !== "sel") g.querySelectorAll(".t1.pick[data-id]").forEach(r => { delete this.s.keep[r.dataset.id] })
    }
    this._apply()
  }

  // The division's common CSI cost codes the job doesn't have: drawn the
  // first time they're needed (opening it, or a search).
  _ensureSecs(g) {
    if (g.dataset.secsDone === "1") return
    g.dataset.secsDone = "1"
    let secs = []
    try { secs = JSON.parse(g.dataset.secs || "[]") } catch (e) { secs = [] }
    const yours = g.dataset.div === "01"
    const picks = Array.from(g.querySelectorAll(":scope > .t1.pick"))
    const add = g.querySelector(":scope > .t1add")
    secs.forEach(([code, name]) => {
      const row = document.createElement("div")
      row.className = "t1 avail pick hidden"
      row.dataset.st = "sec"
      row.dataset.code = code
      row.dataset.new = "1"
      if (yours) row.dataset.yours = "1"
      row.innerHTML = `<label class="cb"><input type="checkbox" aria-label="${esc(this._t("rows.add", { code: `${code} ${name}` }))}"></label><span class="code c-code" data-hl="${esc(code)}">${esc(code)}<mark class="hit"></mark></span><span class="lnm"><span class="trunc" data-hl="${esc(name)}">${esc(name)}<mark class="hit"></mark></span></span><span class="c-type muted"></span><span class="muted trunc c-src"></span><span class="r c-var" title=""><span class="aivar none">—</span></span><span class="r c-est" title="">—</span><span class="r c-ai aiest" title="">—</span>`
      const next = picks.find(r => r.dataset.code > code)
      g.insertBefore(row, next || add)
    })
  }

  _label(row) { return `${row.dataset.code} ${row.querySelector(".lnm .trunc")?.dataset.hl || ""}` }

  _hay(row, g) {
    if (!row._hay) row._hay = `${this._label(row)} division ${g.dataset.label}`.toLowerCase()
    return row._hay
  }

  // Search: a number like "07 54" finds cost codes that start with it; words
  // look in the description and the division name.
  _matcher() {
    const q = this.s.query.trim().toLowerCase()
    const qs = q.replace(/[\s.-]/g, "")
    const isCode = q.length > 0 && /^\d+$/.test(qs)
    const words = q.split(/\s+/).filter(Boolean)
    return {
      q, qs, isCode, words,
      test: (code, hay) => {
        if (!q) return true
        if (isCode) return String(code || "").replace(/[\s.-]/g, "").startsWith(qs)
        return words.every(w => hay.includes(w))
      }
    }
  }

  _hl(el, m, kind) {
    const text = el.dataset.hl
    let pre = text, hit = "", post = ""
    if (m.q) {
      if (kind === "code" && m.isCode && text.replace(/[\s.-]/g, "").startsWith(m.qs)) {
        let n = 0, at = 0
        while (at < text.length && n < m.qs.length) { if (!/[\s.-]/.test(text[at])) n += 1; at += 1 }
        pre = ""; hit = text.slice(0, at); post = text.slice(at)
      } else if (kind === "name" && !m.isCode) {
        const low = text.toLowerCase()
        let best = null
        m.words.forEach(w => { const i = low.indexOf(w); if (i >= 0 && (!best || w.length > best[1])) best = [i, w.length] })
        if (best) { pre = text.slice(0, best[0]); hit = text.slice(best[0], best[0] + best[1]); post = text.slice(best[0] + best[1]) }
      }
    }
    const html = `${esc(pre)}<mark class="hit">${esc(hit)}</mark>${esc(post)}`
    if (el.innerHTML !== html) el.innerHTML = html
  }

  // Shows and hides the By division rows from STATE (no server call).
  _apply() {
    if (!this.hasDivsTarget) return
    const m = this._matcher()
    const searching = m.q.length > 0
    const sel = this.s.filter === "sel"
    let hits = 0, hitDivs = 0, shown = 0
    this.divsTarget.querySelectorAll(":scope > .s1grp").forEach(g => {
      const div = g.dataset.div
      if (searching || this.s.open[div]) this._ensureSecs(g)
      const rows = Array.from(g.querySelectorAll(":scope > .t1.pick"))
      const on = rows.some(r => r.dataset.st === "on")
      const kept = rows.some(r => this.s.keep[r.dataset.id])
      let show = !(sel && !on && !kept && g.dataset.on !== "1")
      let open = !!this.s.open[div]
      let rowHits = rows
      if (show && searching) {
        rowHits = rows.filter(r => m.test(r.dataset.code, this._hay(r, g)))
        if (!rowHits.length && !m.test(div, `${div} division ${g.dataset.label}`.toLowerCase())) show = false
        open = !this.s.shut[div]
      }
      g.classList.toggle("hidden", !show)
      if (!show) return
      shown += 1
      if (searching) { hits += rowHits.length; hitDivs += 1 }
      const divrow = g.querySelector(":scope > .divrow")
      divrow.querySelector(".chev")?.classList.toggle("open", open)
      g.querySelectorAll(":scope > .divrow [data-hl]").forEach((el, i) => this._hl(el, m, i === 0 ? "code" : "name"))
      const visible = {}
      rows.forEach(r => {
        let vis
        if (searching) vis = open && rowHits.includes(r)
        else if (open) vis = true
        else vis = r.dataset.st === "on" || !!(r.dataset.id && this.s.keep[r.dataset.id])
        r.classList.toggle("hidden", !vis)
        if (vis) {
          visible[r.dataset.id] = r.dataset.st === "on"
          r.querySelectorAll("[data-hl]").forEach((el, i) => this._hl(el, m, i === 0 ? "code" : "name"))
        }
        const tplOpen = !!(r.dataset.id && this.s.tpl[r.dataset.id]) && vis && r.dataset.st === "on"
        r.classList.toggle("tplopen", tplOpen)
        const chip = r.querySelector(".tplchip")
        if (chip) {
          chip.setAttribute("aria-expanded", tplOpen)
          chip.querySelector(".tchev")?.classList.toggle("open", tplOpen)
          if (!chip.dataset.tip) chip.dataset.tip = chip.title
          chip.title = tplOpen ? this._t("tpl.tip_open") : chip.dataset.tip
          chip.setAttribute("aria-label", this._t(tplOpen ? "tpl.aria_hide" : "tpl.aria_show", { code: this._label(r) }))
        }
      })
      g.querySelectorAll(":scope > .t1note").forEach(n => n.classList.toggle("hidden", searching || !visible[n.dataset.for]))
      rows.forEach(r => { if (r.dataset.tpl && this.s.tpl[r.dataset.id] && visible[r.dataset.id]) this._ensureTpl(r) })
      g.querySelectorAll(":scope > .t1tpl").forEach(t => t.classList.toggle("hidden", !(visible[t.dataset.tplFor] && this.s.tpl[t.dataset.tplFor])))
      g.querySelector(":scope > .t1add")?.classList.toggle("hidden", searching || !open)
    })
    if (this.hasClearTarget) this.clearTarget.classList.toggle("hidden", !searching)
    if (this.hasHitsTarget) {
      this.hitsTarget.textContent = hitDivs ? this._t("tools.hits", { codes: this._t("n_codes", { count: hits }), divisions: this._t("n_divisions", { count: hitDivs }) }) : this._t("tools.no_matches")
      this.hitsTarget.classList.toggle("hidden", !searching || this.element.dataset.view !== "division")
    }
    if (this.hasEmptyTarget) {
      const empty = (searching || sel) && shown === 0
      this.emptyTarget.classList.toggle("hidden", !empty)
      if (empty) {
        const q = m.q
        const [title, text, btn] = !searching ? ["rows.empty_none_title", "rows.empty_none_text", "rows.empty_none_btn"]
          : (sel ? ["rows.empty_sel_title", "rows.empty_sel_text", "rows.empty_sel_btn"] : ["rows.empty_all_title", "rows.empty_all_text", "rows.empty_all_btn"])
        this.emptyTarget.innerHTML = `<b>${esc(this._t(title, { q: this.s.query.trim() }))}</b> ${esc(this._t(text))} <button class="linkbtn" type="button">${esc(this._t(btn))}</button>`
        this.emptyTarget.querySelector("button").addEventListener("click", () => {
          if (!q || sel) { this.s.filter = "all"; this.s.keep = {}; this.s.shut = {}; this.element.querySelectorAll(".fchip[data-filter]").forEach(c => c.classList.toggle("on", c.dataset.filter === "all")); this._apply() } else this.clearSearch()
        })
      }
    }
  }

  // ── Ticking cost codes and divisions (optimistic) ──────────────────────
  _toggleTpl(id) {
    if (this.s.tpl[id]) delete this.s.tpl[id]; else this.s.tpl[id] = true
    this._apply()
  }

  // A cost code's bid template, open under its row (the prototype's t1tpl).
  _ensureTpl(row) {
    const g = row.closest(".s1grp")
    if (g.querySelector(`:scope > .t1tpl[data-tpl-for='${row.dataset.id}']`)) return
    const d = this._tpls[row.dataset.id]
    if (!d) return
    const box = document.createElement("div")
    box.className = `t1tpl ${row.dataset.new === "1" ? "luser" : "lai"} hidden`
    box.dataset.tplFor = row.dataset.id
    const pencil = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"></path></svg>`
    let html = `<div class="tpx-h"><span class="badge b-ai">${SVG.sparkles}${esc(this._t("tpl.badge"))}</span><span class="muted tpx-src">${esc(d.src)}</span><button class="btn btn-secondary btn-sm" type="button" data-tpl-edit>${pencil}${esc(d.off ? this._t("tpl.turn_on") : this._t("tpl.edit"))}</button></div>`
    if (d.off) {
      html += `<div class="tpx-f">${esc(this._t("tpl.off_text"))}</div>`
    } else {
      html += `<div class="tpx-lines">${d.lines.map((l, i) => `<div class="tpx-l"><span class="tpln">${i + 1}</span><span class="tpx-nm"><span>${esc(l.name)}</span>${l.added ? `<span class="badge b-brand">${esc(this._t("tpl.added_by_you"))}</span>` : ""}</span><span class="tpx-est">${l.est ? money(l.est) : "—"}</span></div>`).join("")}</div>`
      if (d.note) html += `<div class="tpx-note"><b>${esc(this._t("tpl.note_label"))}</b> ${esc(d.note)}</div>`
      html += `<div class="tpx-f">${esc(this._t("tpl.foot", { lines: this._t("n_lines", { count: d.lines.length }), amount: money(d.est || 0) }))}</div>`
    }
    box.innerHTML = html
    box.dataset.id = row.dataset.id
    let after = row
    const note = g.querySelector(`:scope > .t1note[data-for='${row.dataset.id}']`)
    if (note) after = note
    after.after(box)
  }

  _flip(row, want) {
    const was = row.dataset.st
    const on = want === undefined ? was !== "on" : want
    if ((was === "on") === on) return
    // Just added and not saved yet: wait for the server's row.
    if (!on && !row.dataset.id) { const cb = row.querySelector("input[type=checkbox]"); if (cb) cb.checked = true; return }
    const g = row.closest(".s1grp")
    this._paintRow(row, on)
    if (!on) this.s.keep[row.dataset.id] = true
    this._recount(g)
    this._apply()
    if (was === "sec") {
      this._send("POST", "cost_codes", { code: row.dataset.code, division: g.dataset.div })
    } else {
      this._send("PATCH", `cost_codes/${row.dataset.id}`, { active: on ? "1" : "0" })
    }
  }

  _paintRow(row, on) {
    const isNew = row.dataset.new === "1"
    row.dataset.st = on ? "on" : (row.dataset.id ? "off" : "sec")
    row.classList.remove("lai", "luser", "avail")
    row.classList.add(on ? (isNew ? "luser" : "lai") : "avail")
    const cb = row.querySelector("input[type=checkbox]")
    if (cb) {
      cb.checked = on
      cb.setAttribute("aria-label", this._t(on ? "rows.remove" : "rows.add", { code: this._label(row) }))
    }
    row.querySelectorAll(".lnm .badge").forEach(b => b.classList.toggle("hidden", !on))
    const type = row.querySelector(".c-type")
    if (type) {
      type.textContent = on ? (row.dataset.yours === "1" ? this._t("yours") : this._t("bid_out")) : (isNew ? "" : this._t("removed_by_you"))
      type.classList.toggle("muted", on || isNew)
      type.classList.toggle("removed", !on && !isNew)
    }
    const est = row.dataset.est ? Number(row.dataset.est) : null
    const ai = row.dataset.ai === "1"
    const cEst = row.querySelector(".c-est")
    if (cEst) { cEst.textContent = on && est !== null ? money(est) : "—"; cEst.classList.toggle("aiv", on && est !== null && ai) }
    const cAi = row.querySelector(".c-ai")
    if (cAi) cAi.textContent = on && est !== null ? money(est) : "—"
    const v = row.querySelector(".c-var .aivar")
    if (v) {
      const psf = on && est !== null && this.sfValue > 0
      v.className = psf ? `aivar psf${ai ? " aiv" : ""}` : "aivar none"
      v.textContent = psf ? "$" + (est / this.sfValue).toFixed(2) : "—"
    }
  }

  // A division row's checkbox, count and total after a tick.
  _recount(g) {
    const rows = Array.from(g.querySelectorAll(":scope > .t1.pick"))
    const onRows = rows.filter(r => r.dataset.st === "on")
    const on = onRows.length > 0
    const div = g.querySelector(":scope > .divrow")
    div.classList.toggle("don", on)
    div.classList.toggle("coll", !on)
    const cb = div.querySelector("input[type=checkbox]")
    if (cb) { cb.checked = on; cb.setAttribute("aria-label", this._t(on ? "rows.div_uncheck" : "rows.div_check", { div: g.dataset.label })) }
    const count = div.querySelector(".dcount")
    if (count) count.textContent = on ? this._t("n_of_total", { on: onRows.length, total: g.dataset.total }) : this._t("not_in_job")
    const badge = div.querySelector(".c-type .badge")
    if (badge) {
      const yours = g.dataset.div === "01"
      badge.className = `badge ${on ? (yours ? "b-green" : "b-blue") : "nochip"}`
      badge.textContent = on ? (yours ? this._t("yours") : this._t("bid_out")) : ""
    }
    const sum = onRows.reduce((a, r) => a + (Number(r.dataset.est) || 0), 0)
    const ai = on && onRows.every(r => r.dataset.ai === "1")
    div.querySelectorAll(".c-est, .c-ai").forEach(el => { el.textContent = on ? money(sum) : "—" })
    div.querySelector(".c-est")?.classList.toggle("aiv", ai)
    const v = div.querySelector(".c-var .aivar")
    if (v) {
      const psf = on && sum > 0 && this.sfValue > 0
      v.className = psf ? `aivar psf${ai ? " aiv" : ""}` : "aivar none"
      v.textContent = psf ? "$" + (sum / this.sfValue).toFixed(2) : "—"
    }
  }

  _divToggle(cb) {
    const g = cb.closest(".s1grp")
    const on = cb.checked
    if (on) this._ensureSecs(g)
    g.querySelectorAll(":scope > .t1.pick").forEach(r => {
      if (on) this._paintRow(r, true)
      else if (r.dataset.st === "on") { this._paintRow(r, false); this.s.keep[r.dataset.id] = true }
    })
    this._recount(g)
    this._apply()
    track(on ? "Precon Division Checked" : "Precon Division Unchecked", { project_id: this.projectValue, division: g.dataset.div })
    this._send("POST", "cost_codes/division", { division: g.dataset.div, on: on ? "1" : "0" })
  }

  // ── Sort: CSI order | Order of construction ─────────────────────────────
  sort(e) {
    const sort = e.currentTarget.dataset.sort
    if (sort === this.s.sort) return
    this._reorder(sort)
    track("Precon Budget Sorted", { project_id: this.projectValue, sort })
    this._send("PATCH", "cost_codes/settings", { sort_build: sort === "build" ? "1" : "0" })
  }

  _reorder(sort) {
    this.s.sort = sort
    this.element.querySelectorAll(".sortseg .segbtn").forEach(b => { const on = b.dataset.sort === sort; b.classList.toggle("on", on); b.setAttribute("aria-pressed", on) })
    const key = sort === "build" ? "build" : "csi"
    if (this.hasDivsTarget) {
      Array.from(this.divsTarget.querySelectorAll(":scope > .s1grp"))
        .sort((a, b) => Number(a.dataset[key]) - Number(b.dataset[key]))
        .forEach(g => this.divsTarget.appendChild(g))
    }
    if (this.hasPkgsTarget) {
      const nk = sort === "build" ? "nBuild" : "nCsi"
      Array.from(this.pkgsTarget.querySelectorAll(":scope > .s1grp"))
        .sort((a, b) => Number(a.dataset[nk]) - Number(b.dataset[nk]))
        .forEach(g => { this.pkgsTarget.appendChild(g); const n = g.querySelector(".bpnum"); if (n) n.textContent = g.dataset[nk] })
    }
  }

  // ── Columns (per person) ───────────────────────────────────────────────
  _cols() {
    let saved = {}
    try { saved = JSON.parse(store.get("conslog.app.precon.s1cols") || "{}") || {} } catch (e) { saved = {} }
    return Object.assign({}, COLS_DEFAULT, saved, { ai: false })
  }

  _applyCols() {
    if (!this.hasCardTarget) return
    const c = this._cols()
    const card = this.cardTarget
    Array.from(card.classList).filter(k => /^g-[01]{6}$/.test(k) || /^hide-/.test(k)).forEach(k => card.classList.remove(k))
    card.classList.add("g-" + COL_KEYS.map(k => (c[k] ? "1" : "0")).join(""))
    COL_KEYS.filter(k => !c[k]).forEach(k => card.classList.add("hide-" + k))
    this.element.querySelectorAll("[data-col]").forEach(cb => { cb.checked = !!c[cb.dataset.col] })
  }

  toggleCols() {
    if (!this.hasColMenuTarget) return
    const open = this.colMenuTarget.classList.contains("hidden")
    this.colMenuTarget.classList.toggle("hidden", !open)
    if (this.hasColBtnTarget) this.colBtnTarget.classList.toggle("on", open)
  }

  setCol(e) {
    const c = this._cols()
    c[e.currentTarget.dataset.col] = e.currentTarget.checked
    store.set("conslog.app.precon.s1cols", JSON.stringify(c))
    this._applyCols()
    track("Precon Budget Columns Changed", { project_id: this.projectValue, column: e.currentTarget.dataset.col, shown: e.currentTarget.checked })
  }

  // ── Pop-ups (the prototype's .overlay > .modal) ────────────────────────
  _modal({ title, sub, body, foot, cls = "", label }) {
    this._closeModal()
    const host = this.element.closest(".precon-proto") || this.page?.element || document.body
    const wrap = document.createElement("div")
    wrap.className = "overlay"
    wrap.innerHTML = `<div class="modal ${cls}" role="dialog" aria-label="${esc(label || title)}">
      <div class="mhead"><div><h2 class="cardtitle">${esc(title)}</h2>${sub ? `<div class="cardsub">${esc(sub)}</div>` : ""}</div><button class="x" type="button" data-close aria-label="${esc(this._t("close"))}">${SVG.x}</button></div>
      ${body}
      ${foot}
    </div>`
    wrap.addEventListener("click", (e) => { if (e.target === wrap || e.target.closest("[data-close]")) this._closeModal() })
    host.appendChild(wrap)
    MODAL.el = wrap
    return wrap
  }

  _closeModal() {
    if (MODAL.el) { MODAL.el.remove(); MODAL.el = null }
  }

  // Bid templates on/off — asks first.
  askTemplates(e) {
    const turnOn = e.currentTarget.checked
    e.currentTarget.checked = !turnOn
    const k = turnOn ? "on" : "off"
    const m = this._modal({
      title: this._t(`tf.${k}_title`),
      body: `<div class="pad col12"><span>${esc(this._t(`tf.${k}_text`))}</span><span class="muted">${esc(this._t(`tf.${k}_sub`))}</span></div>`,
      foot: `<div class="mfoot"><button class="btn btn-secondary" type="button" data-close>${esc(this._t("cancel"))}</button><button class="btn btn-primary" type="button" data-ok>${esc(this._t(`tf.${k}_btn`))}</button></div>`
    })
    m.querySelector("[data-ok]").addEventListener("click", () => {
      this._closeModal()
      const label = this.element.querySelector(".tplfeat")
      if (label) { label.classList.toggle("on", turnOn); label.querySelector("input").checked = turnOn }
      this.s.tpl = {}
      track(turnOn ? "Precon Bid Templates Turned On" : "Precon Bid Templates Turned Off", { project_id: this.projectValue })
      this._send("PATCH", "cost_codes/settings", { bid_templates_on: turnOn ? "1" : "0" }, this._t(`tf.${k}_toast`))
    })
    m.querySelector("[data-ok]").focus()
  }

  // Order of construction: the divisions in this job, up and down.
  openOrder() {
    const o = this._json.order || {}
    const inJob = new Set(o.in_job || [])
    let draft = (o.order || o.default || []).slice()
    const m = this._modal({
      title: this._t("order.title"), sub: this._t("order.sub"),
      body: `<div class="pad col12"><div class="ordlist" data-list></div><div><button class="linkbtn" type="button" data-reset>${esc(this._t("order.reset"))}</button></div></div>`,
      foot: `<div class="mfoot"><button class="btn btn-secondary" type="button" data-close>${esc(this._t("cancel"))}</button><button class="btn btn-primary" type="button" data-save>${esc(this._t("order.save"))}</button></div>`
    })
    const list = m.querySelector("[data-list]")
    const draw = () => {
      const shown = draft.filter(c => inJob.has(c))
      list.innerHTML = shown.map((c, i) => {
        const name = `${c} ${(o.names || {})[c] || ""}`
        return `<div class="ordrow"><span class="ordn">${i + 1}</span><span class="code">${esc(c)}</span><span class="ordname">${esc((o.names || {})[c] || "")}</span><button class="ordbtn" type="button" data-move="${c}" data-dir="-1" ${i === 0 ? "disabled" : ""} aria-label="${esc(this._t("order.up", { div: name }))}">${SVG.up}</button><button class="ordbtn" type="button" data-move="${c}" data-dir="1" ${i === shown.length - 1 ? "disabled" : ""} aria-label="${esc(this._t("order.down", { div: name }))}">${SVG.down}</button></div>`
      }).join("")
    }
    draw()
    list.addEventListener("click", (e) => {
      const b = e.target.closest("[data-move]")
      if (!b) return
      const c = b.dataset.move
      const vis = draft.filter(x => inJob.has(x))
      const other = vis[vis.indexOf(c) + Number(b.dataset.dir)]
      if (!other) return
      const i1 = draft.indexOf(c), i2 = draft.indexOf(other)
      draft[i1] = other; draft[i2] = c
      draw()
    })
    m.querySelector("[data-reset]").addEventListener("click", () => { draft = (o.default || []).slice(); draw() })
    m.querySelector("[data-save]").addEventListener("click", () => {
      this._closeModal()
      this._json.order = Object.assign({}, o, { order: draft })
      track("Precon Build Order Saved", { project_id: this.projectValue })
      this._send("PATCH", "cost_codes/settings", { build_order: draft, sort_build: "1" }, this._t("order.saved"))
    })
  }

  // The bid template pop-up for one cost code (from its open row).
  openTemplate(box) {
    const d = Object.assign({}, this._tpls[box.dataset.id], { id: box.dataset.id })
    const draft = { lines: d.lines.map(l => Object.assign({}, l)), on: !d.off, note: d.note || "" }
    const m = this._modal({
      title: this._t("tpl.title", { code: d.code }), sub: this._t("tpl.sub"), cls: "wide",
      body: `<div class="pad col12">
        <div class="ai">${SVG.sparkles}<div><b>${esc(d.src)}</b></div></div>
        <div class="tplrows" data-rows></div>
        <div class="tplideas"><button class="linkbtn" type="button" data-add>${esc(this._t("tpl.add"))}</button></div>
        <div class="ai warn hidden" data-gap>${SVG.warn}<div>${esc(this._t("tpl.gap", { count: d.bids }))}</div></div>
        <div class="tplsw"><label class="toggle"><input type="checkbox" data-on ${draft.on ? "checked" : ""}><span>${esc(this._t("tpl.toggle"))}</span></label><span class="muted" data-ontext></span></div>
        <label class="fl">${esc(this._t("tpl.note"))}<textarea class="inp ta tplta" data-note placeholder="${esc(this._t("tpl.note_ph"))}">${esc(draft.note)}</textarea></label>
      </div>`,
      foot: `<div class="mfoot spread"><span class="tplfoot" data-foot></span><span class="row8"><button class="btn btn-secondary" type="button" data-close>${esc(this._t("cancel"))}</button><button class="btn btn-primary" type="button" data-save>${esc(this._t("tpl.save"))}</button></span></div>`
    })
    const rows = m.querySelector("[data-rows]")
    const draw = () => {
      rows.classList.toggle("off", !draft.on)
      rows.innerHTML = `<div class="tplhead"><span></span><span>${esc(this._t("tpl.head_lines"))}</span><span class="r">${esc(this._t("tpl.head_est"))}</span><span></span></div>` +
        draft.lines.map((l, i) => {
          const renamed = !l.added && l.ai_name && l.ai_name !== l.name
          const orig = l.added ? this._t("tpl.added_by_you") : (renamed ? this._t("tpl.ai_wrote", { name: l.ai_name }) : "")
          return `<div class="tplrow"><span class="tpln">${i + 1}</span><input class="inp" type="text" value="${esc(l.name)}" data-i="${i}" placeholder="${esc(l.added ? this._t("tpl.new_ph") : (l.ai_name || l.name))}" aria-label="${esc(this._t("tpl.line_aria", { n: i + 1 }))}"><span class="tplest" title="${l.est ? esc(this._t("tpl.est_tip", { name: l.name })) : ""}">${l.est ? money(l.est) : "—"}</span><span>${l.added ? `<button class="setx" type="button" data-rm="${i}" aria-label="${esc(this._t("tpl.remove_aria", { n: i + 1 }))}">${SVG.x}</button>` : ""}</span>${orig ? `<span class="tplorig"><span>${esc(orig)}</span>${renamed ? `<button class="linkbtn" type="button" data-revert="${i}">${esc(this._t("tpl.use_ai_name"))}</button>` : ""}</span>` : ""}</div>`
        }).join("")
      const named = draft.lines.filter(l => String(l.name || "").trim())
      const sum = draft.lines.reduce((a, l) => a + (Number(l.est) || 0), 0)
      m.querySelector("[data-foot]").textContent = this._t("tpl.modal_foot", { lines: this._t("n_lines", { count: named.length }), amount: money(sum) })
      m.querySelector("[data-ontext]").textContent = draft.on ? this._t("tpl.on_text") : this._t("tpl.off_text_modal")
      m.querySelector("[data-gap]").classList.toggle("hidden", !(draft.on && d.bids > 0 && draft.lines.some(l => l.added && String(l.name || "").trim() && !l.saved)))
    }
    d.lines.forEach(l => { if (l.added) l.saved = true })
    draft.lines.forEach(l => { if (l.added) l.saved = true })
    draw()
    rows.addEventListener("input", (ev) => {
      const i = ev.target.dataset.i
      if (i === undefined) return
      draft.lines[Number(i)].name = ev.target.value
      const named = draft.lines.filter(l => String(l.name || "").trim())
      m.querySelector("[data-foot]").textContent = this._t("tpl.modal_foot", { lines: this._t("n_lines", { count: named.length }), amount: money(draft.lines.reduce((a, l) => a + (Number(l.est) || 0), 0)) })
    })
    rows.addEventListener("change", (ev) => { if (ev.target.dataset.i !== undefined) draw() })
    rows.addEventListener("click", (ev) => {
      const rm = ev.target.closest("[data-rm]")
      if (rm) { draft.lines.splice(Number(rm.dataset.rm), 1); draw(); return }
      const rv = ev.target.closest("[data-revert]")
      if (rv) { const l = draft.lines[Number(rv.dataset.revert)]; l.name = l.ai_name; draw() }
    })
    m.querySelector("[data-add]").addEventListener("click", () => {
      draft.lines.push({ name: "", ai_name: null, added: true, est: null })
      draw()
      const inputs = rows.querySelectorAll("input[data-i]")
      inputs[inputs.length - 1]?.focus()
    })
    m.querySelector("[data-on]").addEventListener("change", (ev) => { draft.on = ev.target.checked; draw() })
    m.querySelector("[data-note]").addEventListener("input", (ev) => { draft.note = ev.target.value })
    m.querySelector("[data-save]").addEventListener("click", () => {
      const lines = draft.lines.filter(l => !(l.added && !String(l.name || "").trim()))
      if (lines.some(l => !String(l.name || "").trim())) { this.page?._showToast(this._t("tpl.line_needed"), "error"); return }
      const fresh = lines.filter(l => l.added && !l.saved).length
      this._closeModal()
      track("Precon Bid Template Edited", { project_id: this.projectValue, lines: lines.length, off: !draft.on })
      const ok = !draft.on ? this._t("tpl.saved_off", { code: d.code })
        : this._t("tpl.saved", { code: d.code, lines: this._t("n_lines", { count: lines.length }) }) + (fresh && d.bids ? this._t("tpl.saved_gap") : "")
      this._send("PATCH", `cost_codes/${d.id}`, { template: { lines: lines.map(l => ({ name: l.name.trim(), added: !!l.added })), off: !draft.on, note: draft.note } }, ok)
    })
  }

  // "Add a line item": the division's full CSI list, or your own line.
  async _loadCsi() {
    if (CSI.list) return CSI.list
    if (!CSI.loading) {
      const page = this.page
      if (!page) return null
      CSI.loading = page._json("GET", `${page.baseUrlValue}/cost_codes/csi`).then(d => { CSI.list = d; return d }).catch(() => { CSI.loading = null; return null })
    }
    return CSI.loading
  }

  async openPick(g) {
    const div = g.dataset.div
    const have = new Set(Array.from(g.querySelectorAll(":scope > .t1.pick[data-st='on']")).map(r => r.dataset.code))
    const csi = await this._loadCsi()
    const all = (csi?.sections?.[div] || [])
    const sel = new Set()
    let query = ""
    const m = this._modal({
      title: this._t("pick.title"), sub: this._t("pick.sub", { div: g.dataset.label }), cls: "wide",
      body: `<div class="pad col12">
        <div class="pickhd"><span class="customhd">${esc(this._t("pick.head"))}</span><span class="muted">${esc(this._t("n_codes", { count: all.length }))}</span></div>
        <span class="search wide">${SVG.search}<input class="inp" type="text" data-q placeholder="${esc(this._t("pick.search_ph"))}" aria-label="${esc(this._t("tools.search_aria"))}"></span>
        <div class="picklist" data-list></div>
        <div class="customhd">${esc(this._t("pick.custom_head"))}</div>
        <div class="customrow"><input class="inp" type="text" data-code placeholder="${esc(div)} 00 00" aria-label="${esc(this._t("pick.code_aria"))}"><input class="inp" type="text" data-name placeholder="${esc(this._t("pick.desc_ph"))}" aria-label="${esc(this._t("pick.desc_aria"))}"><input class="inp" type="text" data-amt placeholder="${esc(this._t("pick.amount_ph"))}" aria-label="${esc(this._t("pick.amount_aria"))}"></div>
      </div>`,
      foot: `<div class="mfoot"><button class="btn btn-secondary" type="button" data-close>${esc(this._t("cancel"))}</button><button class="btn btn-primary" type="button" data-add disabled></button></div>`
    })
    const list = m.querySelector("[data-list]")
    const nameIn = m.querySelector("[data-name]")
    const addBtn = m.querySelector("[data-add]")
    const count = () => sel.size + (nameIn.value.trim() ? 1 : 0)
    const paintBtn = () => { const n = count(); addBtn.disabled = n === 0; addBtn.textContent = n ? this._t("pick.add", { count: n }) : this._t("pick.add_none") }
    const draw = () => {
      const q = query.trim().toLowerCase()
      const qs = q.replace(/[\s.-]/g, "")
      const hit = (x) => !q || String(x[1]).toLowerCase().includes(q) || (/^\d+$/.test(qs) && x[0].replace(/\s/g, "").startsWith(qs))
      const rows = all.filter(hit)
      list.innerHTML = rows.map(x => {
        const h = have.has(x[0]); const on = h || sel.has(x[0])
        return `<label class="pickrow ${h ? "have" : (on ? "on" : "")}"><input type="checkbox" data-c="${esc(x[0])}" ${on ? "checked" : ""} ${h ? "disabled" : ""}><span class="code">${esc(x[0])}</span><span>${esc(x[1])}</span></label>`
      }).join("") + (rows.length ? "" : `<div class="muted pickempty">${esc(this._t("pick.empty", { q: query.trim() }))}<button class="linkbtn" type="button" data-own>${esc(this._t("pick.to_own"))}</button></div>`)
      paintBtn()
    }
    draw()
    m.querySelector("[data-q]").addEventListener("input", (ev) => { query = ev.target.value; draw() })
    list.addEventListener("change", (ev) => {
      const c = ev.target.dataset.c
      if (!c) return
      if (ev.target.checked) sel.add(c); else sel.delete(c)
      ev.target.closest(".pickrow").classList.toggle("on", ev.target.checked)
      paintBtn()
    })
    list.addEventListener("click", (ev) => { if (ev.target.closest("[data-own]")) { nameIn.value = query.trim(); query = ""; m.querySelector("[data-q]").value = ""; draw(); nameIn.focus() } })
    nameIn.addEventListener("input", paintBtn)
    addBtn.addEventListener("click", () => {
      const n = count()
      if (!n) { this.page?._showToast(this._t("pick.pick_first"), "error"); return }
      const custom = nameIn.value.trim() ? { code: m.querySelector("[data-code]").value, name: nameIn.value.trim(), estimate: m.querySelector("[data-amt]").value } : null
      this._closeModal()
      this.s.open[div] = true
      track("Precon Line Items Added", { project_id: this.projectValue, division: div, count: n, custom: !!custom })
      this._send("POST", "cost_codes", { division: div, add_codes: Array.from(sel), custom }, this._t("rows.added_toast", { count: n, div: g.dataset.label }))
    })
    m.querySelector("[data-q]").focus()
  }

  // Owner's bid form: move a cost code under another owner item.
  openOwnerMove(e) {
    const b = e.currentTarget
    const script = this.element.querySelector("script[data-s1-owner-items]")
    const items = script ? JSON.parse(script.textContent) : []
    const m = this._modal({
      title: this._t("own.move_title", { code: b.dataset.code }), sub: this._t("own.move_sub"), label: this._t("own.move_title", { code: b.dataset.code }),
      body: `<div class="pad"><div class="picklist">${items.map(it => `<button class="omrow ${it.n === b.dataset.here ? "on" : ""}" type="button" data-n="${esc(it.n)}" data-name="${esc(it.name)}"><span class="onum">${esc(it.n)}</span><span>${esc(it.name)}</span>${it.n === b.dataset.here ? `<span class="muted">${esc(this._t("own.here_now"))}</span>` : ""}</button>`).join("")}</div></div>`,
      foot: `<div class="mfoot"><button class="btn btn-secondary" type="button" data-close>${esc(this._t("cancel"))}</button></div>`
    })
    m.querySelector(".picklist").addEventListener("click", (ev) => {
      const row = ev.target.closest("[data-n]")
      if (!row) return
      this._closeModal()
      if (row.dataset.n === b.dataset.here) return
      track("Precon Owner Item Moved", { project_id: this.projectValue })
      this._send("PATCH", `cost_codes/${b.dataset.id}`, { owner_item: row.dataset.n }, this._t("own.moved", { code: b.dataset.code, item: `${row.dataset.n} ${row.dataset.name}` }))
    })
  }
}
